import { api } from '@/lib/axios';

export type EventStatus = 'draft' | 'published' | 'completed' | 'cancelled';

export interface Event {
  id: string;
  name: string;
  description?: string;
  eventDate: string;
  location?: string;
  bannerUrl?: string;
  listId?: string;
  status: EventStatus;
  createdAt: string;
  updatedAt: string;
  list?: {
    id: string;
    name: string;
  };
  guests: EventGuest[];
  stats?: {
    totalGuests: number;
    confirmed: number;
    pending: number;
    attended: number;
    invitationsSent: number;
  };
  _count?: {
    guests: number;
  };
}

export interface EventGuest {
  id: string;
  eventId: string;
  clientId: string;
  invitationSent: boolean;
  invitationSentAt?: string;
  confirmed: boolean;
  confirmedAt?: string;
  attended: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  client?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  };
}

export interface CreateEventDto {
  name: string;
  description?: string;
  eventDate: string;
  location?: string;
  bannerUrl?: string;
  listId?: string;
  status?: EventStatus;
}

export interface UpdateEventDto {
  name?: string;
  description?: string;
  eventDate?: string;
  location?: string;
  bannerUrl?: string;
  listId?: string;
  status?: EventStatus;
}

export interface AddGuestsDto {
  clientIds: string[];
}

export interface UpdateGuestDto {
  confirmed?: boolean;
  attended?: boolean;
  notes?: string;
}

export interface EventsResponse {
  data: Event[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const eventsService = {
  async getAll(params?: {
    search?: string;
    status?: EventStatus;
    page?: number;
    limit?: number;
  }): Promise<EventsResponse> {
    const { data } = await api.get('/events', { params });
    return data;
  },

  async getById(id: string): Promise<Event> {
    const { data } = await api.get(`/events/${id}`);
    return data;
  },

  async create(event: CreateEventDto): Promise<Event> {
    const { data } = await api.post('/events', event);
    return data;
  },

  async update(id: string, event: UpdateEventDto): Promise<Event> {
    const { data } = await api.patch(`/events/${id}`, event);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/events/${id}`);
  },

  // Guest management
  async addGuests(eventId: string, guests: AddGuestsDto): Promise<{ message: string; added: number }> {
    const { data } = await api.post(`/events/${eventId}/guests`, guests);
    return data;
  },

  async removeGuest(eventId: string, guestId: string): Promise<void> {
    await api.delete(`/events/${eventId}/guests/${guestId}`);
  },

  async updateGuest(eventId: string, guestId: string, update: UpdateGuestDto): Promise<EventGuest> {
    const { data } = await api.patch(`/events/${eventId}/guests/${guestId}`, update);
    return data;
  },

  async getStats(eventId: string): Promise<{
    totalGuests: number;
    confirmed: number;
    pending: number;
    attended: number;
    invitationsSent: number;
    invitationsPending: number;
  }> {
    const { data } = await api.get(`/events/${eventId}/stats`);
    return data;
  },

  // Upload banner
  async uploadBanner(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data} = await api.post('/upload/event-banner', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  // Public event methods
  async getBySlug(slug: string): Promise<{
    id: string;
    name: string;
    slug: string;
    description?: string;
    eventDate: string;
    location?: string;
    bannerUrl?: string;
    status: EventStatus;
    guestCount: number;
  }> {
    const { data } = await api.get(`/events/public/slug/${slug}`);
    return data;
  },

  async confirmGuest(token: string): Promise<{
    message: string;
    guest: {
      name: string;
      email?: string;
      confirmed: boolean;
      confirmedAt?: string;
    };
    event: {
      name: string;
      slug: string;
      eventDate: string;
      location?: string;
    };
  }> {
    const { data } = await api.post(`/events/public/confirm/${token}`);
    return data;
  },

  async declineGuest(token: string): Promise<{
    message: string;
    guest: {
      name: string;
      email?: string;
      declined: boolean;
      declinedAt?: string;
    };
    event: {
      name: string;
      slug: string;
      eventDate: string;
    };
  }> {
    const { data } = await api.post(`/events/public/decline/${token}`);
    return data;
  },

  async getInvitationLink(eventId: string, guestId: string): Promise<{
    confirmationToken: string;
    confirmUrl: string;
    declineUrl: string;
    guest: {
      name: string;
      phone?: string;
      email?: string;
    };
    event: {
      name: string;
      slug: string;
      eventDate: string;
      location?: string;
    };
  }> {
    const { data } = await api.get(`/events/${eventId}/guests/${guestId}/invitation-link`);
    return data;
  },
};
